import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Get all notifications
    getNotificationsStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    getNotificationsSuccess: (state, action) => {
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(
        (notification) => !notification.read
      ).length;
      state.isLoading = false;
    },
    getNotificationsFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Add new notification (from socket)
    addNotification: (state, action) => {
      state.notifications = [action.payload, ...state.notifications];
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },

    // Mark notification as read
    markAsReadStart: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    markAsReadSuccess: (state, action) => {
      const { notificationId } = action.payload;
      const notificationIndex = state.notifications.findIndex(
        (notification) => notification.id === notificationId
      );

      if (notificationIndex !== -1) {
        if (!state.notifications[notificationIndex].read) {
          state.unreadCount -= 1;
        }
        state.notifications[notificationIndex].read = true;
      }

      state.isLoading = false;
    },
    markAsReadFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Mark all notifications as read
    markAllAsReadStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    markAllAsReadSuccess: (state) => {
      state.notifications = state.notifications.map((notification) => ({
        ...notification,
        read: true,
      }));
      state.unreadCount = 0;
      state.isLoading = false;
    },
    markAllAsReadFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Delete notification
    deleteNotificationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    deleteNotificationSuccess: (state, action) => {
      const { notificationId } = action.payload;
      const deletedNotification = state.notifications.find(
        (notification) => notification.id === notificationId
      );

      if (deletedNotification && !deletedNotification.read) {
        state.unreadCount -= 1;
      }

      state.notifications = state.notifications.filter(
        (notification) => notification.id !== notificationId
      );
      state.isLoading = false;
    },
    deleteNotificationFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear all notifications
    clearNotificationsStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    clearNotificationsSuccess: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.isLoading = false;
    },
    clearNotificationsFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  getNotificationsStart,
  getNotificationsSuccess,
  getNotificationsFailed,
  addNotification,
  markAsReadStart,
  markAsReadSuccess,
  markAsReadFailed,
  markAllAsReadStart,
  markAllAsReadSuccess,
  markAllAsReadFailed,
  deleteNotificationStart,
  deleteNotificationSuccess,
  deleteNotificationFailed,
  clearNotificationsStart,
  clearNotificationsSuccess,
  clearNotificationsFailed,
} = notificationSlice.actions;

// Selectors
export const selectNotifications = (state) => state.notifications.notifications;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state) =>
  state.notifications.isLoading;
export const selectNotificationsError = (state) => state.notifications.error;

export const notificationReducer = notificationSlice.reducer;
